import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Loader2, Upload, Download, Archive, RotateCcw, Pencil, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { ASSET_TYPES, assetFileUrl, fmtBytes, slug } from "./constants";

const isImage = (ct) => (ct || "").startsWith("image/");

export default function BrandAssets() {
  const [assets, setAssets] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterAi, setFilterAi] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ asset_type: ASSET_TYPES[0], title: "", usage_notes: "", approved_for_ai: false });
  const fileRef = useRef();

  const load = () => api.get("/marketing/brand-assets?include_inactive=true").then((r) => setAssets(r.data)).catch(() => setAssets(false));
  useEffect(() => { load(); }, []);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a file"); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("File exceeds 20 MB limit"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("asset_type", form.asset_type);
    fd.append("title", form.title);
    fd.append("usage_notes", form.usage_notes);
    fd.append("approved_for_ai", form.approved_for_ai);
    setBusy(true);
    try {
      await api.post("/marketing/brand-assets", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Asset uploaded"); setUploadOpen(false);
      setForm({ asset_type: ASSET_TYPES[0], title: "", usage_notes: "", approved_for_ai: false });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/marketing/brand-assets/${editing.id}`, {
        title: editing.title, usage_notes: editing.usage_notes, asset_type: editing.asset_type,
        approved_for_ai: editing.approved_for_ai, active: editing.active,
      });
      toast.success("Asset updated"); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed"); }
    finally { setBusy(false); }
  };

  const setActive = async (a, active) => {
    try { await api.put(`/marketing/brand-assets/${a.id}`, { active }); toast.success(active ? "Restored" : "Archived"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  if (assets === null) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#581C87]" /></div>;
  if (assets === false) return <p className="text-sm text-red-600">Failed to load brand assets.</p>;

  const filtered = assets.filter((a) =>
    (filterType === "all" || a.asset_type === filterType) &&
    (filterStatus === "all" || (filterStatus === "active" ? a.active : !a.active)) &&
    (filterAi === "all" || (filterAi === "yes" ? a.approved_for_ai : !a.approved_for_ai)));

  return (
    <div data-testid="brand-assets-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold">Brand Assets</h2>
          <p className="text-sm text-gray-500 mt-1">Logos, photographs and brand elements used to produce finished media. Never upload fabricated students, testimonials or clinical evidence.</p>
        </div>
        <Button onClick={() => setUploadOpen(true)} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="upload-asset-button"><Upload className="h-4 w-4 mr-2" /> Upload Asset</Button>
      </div>

      <div className="flex flex-wrap gap-3 mt-6">
        <Select value={filterType} onValueChange={setFilterType}><SelectTrigger className="w-56" data-testid="filter-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All types</SelectItem>{ASSET_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}><SelectTrigger className="w-40" data-testid="filter-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Archived</SelectItem></SelectContent></Select>
        <Select value={filterAi} onValueChange={setFilterAi}><SelectTrigger className="w-48" data-testid="filter-ai"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Any AI approval</SelectItem><SelectItem value="yes">Approved for AI</SelectItem><SelectItem value="no">Not AI-approved</SelectItem></SelectContent></Select>
      </div>

      {filtered.length === 0 ? (
        <div className="mt-6 bg-white border border-dashed border-gray-200 rounded-xl px-6 py-12 text-center text-gray-400 text-sm" data-testid="assets-empty">No assets match. Upload a logo or brand image to begin.</div>
      ) : (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((a) => (
            <div key={a.id} className={`bg-white border rounded-xl overflow-hidden ${a.active ? "border-gray-200" : "border-gray-100 opacity-70"}`} data-testid={`asset-card-${a.id}`}>
              <div className="h-40 bg-gray-50 flex items-center justify-center overflow-hidden">
                {isImage(a.content_type)
                  ? <img src={assetFileUrl(a.storage_path)} alt={a.title} className="h-full w-full object-contain" data-testid={`asset-preview-${a.id}`} />
                  : <ImageIcon className="h-10 w-10 text-gray-300" />}
              </div>
              <div className="p-3">
                <p className="text-sm font-medium truncate" title={a.title}>{a.title}</p>
                <p className="text-xs text-gray-400">{a.asset_type}</p>
                <p className="text-[11px] text-gray-400 mt-1">{a.content_type} · {fmtBytes(a.size)}</p>
                <p className="text-[11px] text-gray-400">{(a.created_at || "").slice(0, 10)}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {a.approved_for_ai && <span className="rounded-full px-2 py-0.5 text-[10px] bg-[#F3E8FF] text-[#6B21A8]">AI-approved</span>}
                  <span className={`rounded-full px-2 py-0.5 text-[10px] ${a.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{a.active ? "Active" : "Archived"}</span>
                </div>
                <div className="flex gap-1 mt-3">
                  <a href={assetFileUrl(a.storage_path)} download target="_blank" rel="noreferrer" data-testid={`asset-download-${a.id}`}><Button variant="ghost" size="icon" className="h-8 w-8"><Download className="h-4 w-4" /></Button></a>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing({ ...a })} data-testid={`asset-edit-${a.id}`}><Pencil className="h-4 w-4" /></Button>
                  {a.active
                    ? <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600" onClick={() => setActive(a, false)} data-testid={`asset-archive-${a.id}`}><Archive className="h-4 w-4" /></Button>
                    : <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600" onClick={() => setActive(a, true)} data-testid={`asset-restore-${a.id}`}><RotateCcw className="h-4 w-4" /></Button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-lg" data-testid="upload-asset-dialog">
          <DialogHeader><DialogTitle className="font-heading">Upload Brand Asset</DialogTitle><DialogDescription>Images, fonts, PDF up to 20 MB. Type is validated server-side.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2"><Label>File</Label><Input type="file" ref={fileRef} data-testid="asset-file-input" /></div>
            <div className="space-y-2"><Label>Type</Label>
              <Select value={form.asset_type} onValueChange={(v) => setForm((f) => ({ ...f, asset_type: v }))}><SelectTrigger data-testid="asset-type-select"><SelectValue /></SelectTrigger><SelectContent>{ASSET_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="asset-title-input" /></div>
            <div className="space-y-2"><Label>Usage notes</Label><Textarea value={form.usage_notes} onChange={(e) => setForm((f) => ({ ...f, usage_notes: e.target.value }))} data-testid="asset-notes-input" /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.approved_for_ai} onChange={(e) => setForm((f) => ({ ...f, approved_for_ai: e.target.checked }))} className="accent-[#581C87]" data-testid="asset-ai-checkbox" /> Approved for AI use</label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUploadOpen(false)}>Cancel</Button>
            <Button onClick={upload} disabled={busy} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="confirm-upload-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-asset-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit Asset</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 py-2">
              <div className="space-y-2"><Label>Title</Label><Input value={editing.title} onChange={(e) => setEditing((s) => ({ ...s, title: e.target.value }))} data-testid="edit-asset-title" /></div>
              <div className="space-y-2"><Label>Type</Label>
                <Select value={editing.asset_type} onValueChange={(v) => setEditing((s) => ({ ...s, asset_type: v }))}><SelectTrigger data-testid="edit-asset-type"><SelectValue /></SelectTrigger><SelectContent>{ASSET_TYPES.map((t) => <SelectItem key={t} value={`${t}`}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-2"><Label>Usage notes</Label><Textarea value={editing.usage_notes} onChange={(e) => setEditing((s) => ({ ...s, usage_notes: e.target.value }))} data-testid="edit-asset-notes" /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.approved_for_ai} onChange={(e) => setEditing((s) => ({ ...s, approved_for_ai: e.target.checked }))} className="accent-[#581C87]" data-testid="edit-asset-ai" /> Approved for AI use</label>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="save-asset-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
